import { useAtomValue } from "jotai";
import { useCallback, useRef } from "react";
import { default as WaveSurfer, default as WaveSurferRef } from "wavesurfer.js";
import { mediaElementAtom } from "../../atoms/audioAtom";
import { ColorPalette } from "../../stories/theme";
import { DEFAULT_TIME } from "./waveformHooks";

export const enum SkipTimes {
  BACKWARD_FAST = -15,
  LOCKSCREEN_BACKWARD_STEP = -10,
  BACKWARD_STEP = -5,
  FORWARD_STEP = 5,
  LOCKSCREEN_FORWARD_STEP = 10,
  FORWARD_FAST = 15,
}

export const toggleWaveFormColors = (
  currentlyPlayingWaveForm: WaveSurferRef | null,
  isSelected?: boolean,
) => {
  const waveOptions = isSelected
    ? {
        waveColor: ColorPalette.Gray400,
        progressColor: ColorPalette.BoomyOrange400,
      }
    : {
        waveColor: ColorPalette.Gray200,
        progressColor: ColorPalette.BoomyOrange200,
      };
  currentlyPlayingWaveForm?.setOptions(waveOptions);
};

export const convertWaveformDurationToReadableTime = (duration: number) => {
  return `${Math.floor(duration / 60)}:${Math.floor(duration % 60)
    .toString()
    .padStart(2, "0")}`;
};

export const skipSeconds = (
  seconds: SkipTimes,
  audioElement: HTMLAudioElement | null,
) => {
  if (audioElement) {
    audioElement.currentTime = audioElement.currentTime + seconds;
  }
  return;
};

export const useGetCurrentSeekTime = (isSameProject: boolean) => {
  const audioElement = useAtomValue(mediaElementAtom);
  const currentSeekTime = useRef<string>(DEFAULT_TIME);
  if (audioElement === null) return;
  if (!isSameProject) {
    currentSeekTime.current = DEFAULT_TIME;
  } else {
    currentSeekTime.current = convertWaveformDurationToReadableTime(
      audioElement.currentTime,
    );
  }
  return currentSeekTime;
};

export const setWaveSurferSeekTime = (
  currentPosition: number,
  waveSurferRef: WaveSurfer | null,
) => {
  if (!waveSurferRef) return;
  const duration = waveSurferRef.getDuration();

  if (!duration) return;
  if (duration < currentPosition) return;
  if (currentPosition < 0) return;

  const seekToValue = currentPosition / duration;

  waveSurferRef.seekTo(seekToValue);
};

export const useSetWaveSurferSeekTime = (
  currentPosition: number,
  waveSurferRef: WaveSurfer | null,
) =>
  useCallback(
    () => setWaveSurferSeekTime(currentPosition, waveSurferRef),
    [waveSurferRef, currentPosition],
  );
